<template>
  <div id="initialization-error">
    <div class="wrapper">
      <div class="img-wrapper">
        <div class="scaprim-picture">
          <img src="./Logo.png" alt="scaprim primview"/>
        </div>
      </div>
      <div class="infoBox">
        <p><span class="highlighted">Une erreur est survenue</span> lors de l'installation de l'application.</p>

        <!-- Message d'erreur du store -->
        <div v-if="storeError" class="error-reason">
          <span class="error-reason-label">Raison :</span>
          {{ storeError }}
        </div>

        <br/>
        <p>Cliquez sur le bouton ci-dessous pour <span class="highlighted">réinitialiser les données de synchronisation</span> et relancer l'installation.</p>

        <button @click="handleReload" :disabled="resetting">
          <span v-if="resetting">Réinitialisation…</span>
          <span v-else>Recommencer l'installation</span>
        </button>
      </div>

      <!-- Bloc diagnostic -->
      <div class="diagnostic-box">
        <div class="diagnostic-header" @click="showDiagnostic = !showDiagnostic">
          <span>🔍 Informations de diagnostic</span>
          <span class="chevron">{{ showDiagnostic ? '▲' : '▼' }}</span>
        </div>

        <div v-if="showDiagnostic" class="diagnostic-content">

          <!-- Connexion réseau -->
          <div class="diag-section">
            <div class="diag-title">Réseau</div>
            <div class="diag-row">
              <span class="diag-label">Connexion navigateur</span>
              <span :class="isOnline ? 'diag-ok' : 'diag-error'">{{ isOnline ? '✅ En ligne' : '❌ Hors ligne' }}</span>
            </div>
            <div class="diag-row">
              <span class="diag-label">URL API</span>
              <span class="diag-value diag-mono">{{ apiUrl }}</span>
            </div>
          </div>

          <!-- Données de synchronisation IndexedDB -->
          <div class="diag-section">
            <div class="diag-title">Synchronisation IndexedDB</div>

            <div v-if="diagnosticLoading" class="diag-loading">Chargement…</div>

            <template v-else-if="syncEntries.length > 0">
              <div v-for="entry in syncEntries" :key="entry.entity" class="diag-entity">
                <div class="diag-entity-name">{{ entry.entity }}</div>
                <div class="diag-row">
                  <span class="diag-label">Dernière sync</span>
                  <span :class="entry.lastSyncDate ? 'diag-ok' : 'diag-warn'" class="diag-mono">
                    {{ entry.lastSyncDate || '⚠️ Aucune date — resync complète nécessaire' }}
                  </span>
                </div>
                <div class="diag-row" v-if="entry.exportGenerationDate">
                  <span class="diag-label">Export ZIP utilisé</span>
                  <span class="diag-value diag-mono">{{ entry.exportGenerationDate }}</span>
                </div>
                <div class="diag-row diag-alert" v-if="entry.checkpointPage && entry.checkpointPage > 1">
                  <span class="diag-label">⚠️ Checkpoint actif</span>
                  <span class="diag-value">Page {{ entry.checkpointPage }} — sync interrompue depuis <em>{{ entry.checkpointSince }}</em></span>
                </div>
              </div>
            </template>

            <div v-else class="diag-empty">
              Aucune donnée de synchronisation trouvée en IndexedDB.
              <br><em>C'est normal lors du premier lancement.</em>
            </div>
          </div>

        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { useStore } from 'vuex'
import { ref, computed, onMounted } from 'vue'
import { getEnv } from '@/utils/env'

export default {
  name: 'InitializationError',
  setup() {
    const store = useStore()

    const resetting = ref(false)
    const showDiagnostic = ref(false)
    const diagnosticLoading = ref(true)
    const syncEntries = ref([])
    const isOnline = ref(navigator.onLine)
    const apiUrl = ref(getEnv('VUE_APP_API_URL', '/api'))

    // Message d'erreur depuis le store
    const storeError = computed(() => store.state.initialLoading?.error || null)

    /**
     * Réinitialise la table sync d'IndexedDB puis relance le chargement.
     * Cela efface toutes les lastSyncDate, exportGenerationDate et checkpoints
     * pour forcer une resynchronisation complète depuis le serveur.
     */
    const handleReload = async () => {
      resetting.value = true
      try {
        // 1. Vider la table sync d'IndexedDB pour effacer les dates bloquantes
        const { sync } = await import('@/services/dexieService')
        await sync().clear()
      } catch (error) {
        console.warn('[InitializationError] Impossible de vider la table sync:', error.message)
        // On continue quand même — le reload est toujours utile
      } finally {
        resetting.value = false
        // 2. Relancer le processus de chargement initial
        store.dispatch('initialLoading/reload')
      }
    }

    /**
     * Charge les infos de synchronisation depuis IndexedDB pour le diagnostic.
     */
    const loadDiagnostic = async () => {
      diagnosticLoading.value = true
      try {
        const { sync } = await import('@/services/dexieService')
        const entries = await sync().toArray()
        syncEntries.value = entries.sort((a, b) => a.entity.localeCompare(b.entity))
      } catch (error) {
        console.warn('[InitializationError] Impossible de charger le diagnostic:', error.message)
        syncEntries.value = []
      } finally {
        diagnosticLoading.value = false
      }
    }

    onMounted(() => {
      // Mettre à jour le statut réseau en temps réel
      window.addEventListener('online', () => { isOnline.value = true })
      window.addEventListener('offline', () => { isOnline.value = false })
      // Charger le diagnostic IndexedDB
      loadDiagnostic()
    })

    return {
      storeError,
      resetting,
      showDiagnostic,
      diagnosticLoading,
      syncEntries,
      isOnline,
      apiUrl,
      handleReload
    }
  }
}
</script>

<style lang="scss" scoped>
@import './InitializationError.scss';
</style>
